import { api } from "@/lib/api";

const b64uToBuf = (s) => Uint8Array.from(atob(s.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (s.length % 4)) % 4)), (c) => c.charCodeAt(0)).buffer;
const bufToB64u = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

export const passkeysSupported = () =>
  typeof window !== "undefined" && !!window.PublicKeyCredential && !!navigator.credentials;

function toCreationOptions(o) {
  return {
    ...o,
    challenge: b64uToBuf(o.challenge),
    user: { ...o.user, id: b64uToBuf(o.user.id) },
    excludeCredentials: (o.excludeCredentials || []).map((c) => ({ ...c, id: b64uToBuf(c.id) })),
  };
}

function toRequestOptions(o) {
  return {
    ...o,
    challenge: b64uToBuf(o.challenge),
    allowCredentials: (o.allowCredentials || []).map((c) => ({ ...c, id: b64uToBuf(c.id) })),
  };
}

function serializeCredential(cred) {
  const r = cred.response;
  const out = {
    id: cred.id,
    rawId: bufToB64u(cred.rawId),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment || null,
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
    response: { clientDataJSON: bufToB64u(r.clientDataJSON) },
  };
  if (r.attestationObject) {
    out.response.attestationObject = bufToB64u(r.attestationObject);
    out.response.transports = r.getTransports ? r.getTransports() : [];
  }
  if (r.authenticatorData) {
    out.response.authenticatorData = bufToB64u(r.authenticatorData);
    out.response.signature = bufToB64u(r.signature);
    out.response.userHandle = r.userHandle ? bufToB64u(r.userHandle) : null;
  }
  return out;
}

function deviceName() {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return "iPhone (Face ID / Touch ID)";
  if (/iPad/.test(ua)) return "iPad (Face ID / Touch ID)";
  if (/Android/.test(ua)) return "Android (Fingerprint)";
  if (/Mac/.test(ua)) return "Mac (Touch ID)";
  if (/Windows/.test(ua)) return "Windows (Windows Hello)";
  return "This device";
}

export async function registerPasskey() {
  if (!passkeysSupported()) throw new Error("This device doesn't support Face ID / Fingerprint login");
  const { data: options } = await api.post("/auth/passkeys/register/options");
  const { ceremonyId, ...pk } = options;
  const credential = await navigator.credentials.create({ publicKey: toCreationOptions(pk) });
  const { data } = await api.post("/auth/passkeys/register/verify", {
    ...serializeCredential(credential), ceremonyId, deviceName: deviceName(),
  });
  return data;
}

export async function loginWithPasskey(email) {
  if (!passkeysSupported()) throw new Error("This device doesn't support Face ID / Fingerprint login");
  const { data: options } = await api.post("/auth/passkeys/login/options", { email });
  const { ceremonyId, ...pk } = options;
  const credential = await navigator.credentials.get({ publicKey: toRequestOptions(pk) });
  const { data } = await api.post("/auth/passkeys/login/verify", {
    ...serializeCredential(credential), ceremonyId,
  });
  return data;
}
